class MissingEnvVarError extends Error {
    constructor(variableName: string | string[]) {
        super(
            `Missing env variable; ${
                Array.isArray(variableName)
                    ? variableName.join(", ")
                    : variableName
            }`,
        );
    }
}
/**
 * Returns env variables with verified (to exist) values
 * Throws when environment variable values are missing
 * @param {Record<string, string | undefined>} envSettings
 */
const checkForMissingEnvVariables = <T extends string>(
    envVariables: Record<T, string>,
): Record<T, string> => {
    const missing = Object.entries(envVariables)
        .map(([name, value]) => (!value ? name : undefined))
        .filter(Boolean) as string[];
    if (missing.length) {
        const missingError = new MissingEnvVarError(missing);
        // eslint-disable-next-line no-console
        console.error(missingError);
        throw missingError;
    }
    return envVariables;
};

/**
 * Loads required environment variable settings \
 * Throws when values are missing
 * @param {Record<string, string>} envSettings
 */
const loadRequiredEnvSettings = <T extends string>(
    envVariables: T[],
    options?: {
        tweakLinebreaks: boolean;
    },
): Record<T, string> => {
    const envSettings = envVariables
        .map(name => [name, process.env[name] as string] as const)
        .reduce(
            (settings, [name, value]) => {
                settings[name] = options?.tweakLinebreaks
                    ? value?.replace(/\\n/gu, "\n")
                    : value;
                return settings;
            },
            {} as Record<T, string>,
        );

    return checkForMissingEnvVariables(envSettings);
};

/**
 * Loads required environment variable settings \
 * Throws when values are missing
 * @param {Record<string, string>} envSettings
 */
const checkEnvSettings = <T extends string>(envVariable: T): boolean => {
    return !!process.env[envVariable];
};

/**
 * Loads required environment variable settings \
 * Throws when values are missing
 * @param {Record<string, string>} envSettings
 */
const loadOptionalEnvSettings = <
    T extends string,
    const DefaultValue extends string | undefined = undefined,
>(
    envVariables: (DefaultValue extends undefined
        ? { name: T; default?: undefined }
        : { name: T; default: DefaultValue })[],
): Record<T, (typeof envVariables)[number]["default"]> => {
    const envSettings = envVariables
        .map(
            variable =>
                [
                    variable.name,
                    process.env[variable.name] ?? variable.default,
                ] as [T, (typeof envVariables)[number]["default"]],
        )
        .reduce(
            (settings, [name, value]) => {
                settings[name] = value;
                return settings;
            },
            {} as Record<T, (typeof envVariables)[number]["default"]>,
        );

    return envSettings;
};

/**
 * Loads required environment and optional variable settings \
 * Throws when required values are missing
 * @param {Record<string, string>} envSettings
 */
const loadEnvSettings = <T extends string>(params: {
    required: T[];
    optional: { name: T; default?: string }[];
}): Record<T, string | undefined> => {
    const required = params.required ?? [];
    const optional = params.optional ?? [];
    const envSettings = {
        ...loadRequiredEnvSettings(required),
        ...loadOptionalEnvSettings(optional),
    };
    return checkForMissingEnvVariables(envSettings);
};

export {
    checkEnvSettings,
    checkForMissingEnvVariables,
    loadEnvSettings,
    loadOptionalEnvSettings,
    loadRequiredEnvSettings,
    MissingEnvVarError,
};
